import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiClient from '../services/api';

const DataContext = createContext();

const initialState = {
  hospitalInfo: {
    name: 'Hearts International Hospital',
    address: 'Hearts international hospital, 189-A Sir Syed Road, Saddar, Rawalpindi, 46000',
    contact: '(051) 4252008',
    mapLink: 'https://maps.app.goo.gl/9oTd3bfM2cVZiGPM8?g_st=aw',
    email: 'info@hih.com.pk',
    nearbyLocation: 'Near Saddar Bazaar, Rawalpindi Cantt, Close to Mall Road',
    showName: true,
    showAddress: true,
    showContact: true,
    showMapLink: true,
    showEmail: true,
    showNearbyLocation: true
  },
  posts: [],
  pages: {
    home: {
      heroTitle: 'Exceptional Healthcare',
      heroSubtitle: 'Compassionate care with advanced medical technology',
      aboutTitle: 'About Hearts International Hospital',
      aboutContent: 'We are committed to providing exceptional healthcare services with compassion and expertise.',
      showHero: true,
      showAbout: true,
      showServices: true,
      showDoctors: true,
      showStats: true
    },
    about: {
      title: 'About Us',
      content: 'Hearts International Hospital has been serving the community with excellence in healthcare for over two decades.',
      mission: 'To provide comprehensive, compassionate healthcare services.',
      vision: 'To be the leading healthcare provider in the region.',
      showMission: true,
      showVision: true,
      showTeam: true,
      showHistory: true
    },
    contact: {
      title: 'Contact Us',
      content: 'Get in touch with us for appointments and inquiries.',
      showForm: true,
      showMap: true,
      showInfo: true
    },
    'ceo-message': {
      title: 'CEO Message',
      name: 'Maj.Gen. ® Mohammad Ashraf Khan',
      position: 'Chief Executive Officer',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400',
      message: 'Hearts International Hospital (HIH) was established by a group of highly qualified Pakistani doctors with extensive training and credentials from the USA and UK. Their vision was to create a modern institution that is both patient- and physician-friendly, where quality health care is delivered in a compassionate, ethical, and professional manner.',
      signature: 'Maj.Gen. ® Mohammad Ashraf Khan'
    }
  },
  jobs: [
    {
      id: '1',
      title: 'Staff Nurse',
      department: 'Emergency',
      location: 'Main Hospital',
      type: 'Full-time',
      description: 'Experienced nurse needed for emergency department.',
      requirements: 'BSN degree, 2+ years experience',
      postedDate: '2024-01-01',
      deadline: '2024-02-01',
      isActive: true
    }
  ],
  jobApplications: [],
  doctors: [], // Will be loaded from database via API
  services: [
    {
      id: '1',
      name: 'Medical Clinics',
      description: 'Comprehensive healthcare services delivered by experienced physicians across various medical specialties.',
      icon: 'medical_services',
      category: 'Medical Clinics',
      isActive: true,
      route: '/services/medical-clinics',
      image: '/Medical-Clinics.webp'
    },
    {
      id: '2',
      name: 'Surgical Clinics',
      description: 'Advanced surgical procedures performed by expert surgeons using cutting-edge technology and techniques.',
      icon: 'healing',
      category: 'Surgical Clinics',
      isActive: true,
      route: '/services/surgical-clinics',
      image: '/surgery-clinic.webp'
    },
    {
      id: '3',
      name: 'Diagnostic Services',
      description: 'State-of-the-art diagnostic testing and imaging services for accurate medical assessments.',
      icon: 'biotech',
      category: 'Diagnostic Services',
      isActive: true,
      route: '/services/diagnostic-services',
      image: '/diagnostic-services.webp'
    },
    {
      id: '4',
      name: 'Emergency Services',
      description: '24/7 emergency care with rapid response teams and advanced life support capabilities.',
      icon: 'emergency',
      category: 'Emergency Services',
      isActive: true,
      route: '/services/emergency-services',
      image: '/emergency-service.webp'
    }
  ],
  boardMembers: [
    {
      id: '1',
      name: 'Dr. Ahmed Hassan',
      position: 'Chairman',
      qualification: 'MBBS, MD',
      experience: '25 years',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300',
      bio: 'Leading healthcare professional with extensive experience in hospital administration.',
      showBio: true,
      showQualification: true,
      showExperience: true
    }
  ],
  teamMembers: [
    {
      id: '1',
      name: 'Dr. Sarah Johnson',
      position: 'Chief Medical Officer',
      department: 'Administration',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300',
      bio: 'Experienced medical professional leading our clinical operations.',
      showBio: true,
      showDepartment: true
    }
  ],
  departments: [], // Will be loaded from database via API
  ceo: null, // Will be loaded from database via API
  departmentsLoaded: false, // Track if departments have been loaded at least once
  doctorsLoaded: false, // Track if doctors have been loaded
  postsLoaded: false, // Track if posts have been loaded
  initialLoadComplete: false // Track if initial critical data load is complete
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_HOSPITAL_INFO':
      return {
        ...state,
        hospitalInfo: { ...state.hospitalInfo, ...action.payload }
      };
    case 'ADD_POST':
      return { ...state, posts: [...state.posts, action.payload] };
    case 'UPDATE_POST':
      return {
        ...state,
        posts: state.posts.map(post => post.id === action.payload.id ? action.payload : post)
      };
    case 'DELETE_POST':
      return {
        ...state,
        posts: state.posts.filter(post => post.id !== action.payload)
      };
    case 'LOAD_POSTS':
      return {
        ...state,
        posts: action.payload,
        postsLoaded: true
      };
    case 'UPDATE_PAGE':
      return {
        ...state,
        pages: { ...state.pages, [action.payload.page]: action.payload.content }
      };
    case 'ADD_JOB':
      return { ...state, jobs: [...state.jobs, action.payload] };
    case 'UPDATE_JOB':
      return {
        ...state,
        jobs: state.jobs.map(job => job.id === action.payload.id ? action.payload : job)
      };
    case 'DELETE_JOB':
      return {
        ...state,
        jobs: state.jobs.filter(job => job.id !== action.payload)
      };
    case 'ADD_JOB_APPLICATION':
    case 'ADD_JOB_APPLICATION_LOCAL':
      return { ...state, jobApplications: [...state.jobApplications, action.payload] };
    case 'UPDATE_JOB_APPLICATION':
      return {
        ...state,
        jobApplications: state.jobApplications.map(app => app.id === action.payload.id ? action.payload : app)
      };
    case 'DELETE_JOB_APPLICATION':
      return {
        ...state,
        jobApplications: state.jobApplications.filter(app => app.id !== action.payload)
      };
    case 'ADD_DOCTOR':
      return { ...state, doctors: [...state.doctors, action.payload] };
    case 'UPDATE_DOCTOR':
      return {
        ...state,
        doctors: state.doctors.map(doctor => doctor.id === action.payload.id ? action.payload : doctor)
      };
    case 'DELETE_DOCTOR':
      return {
        ...state,
        doctors: state.doctors.filter(doctor => doctor.id !== action.payload)
      };
    case 'REORDER_DOCTORS':
      return {
        ...state,
        doctors: action.payload
      };
    case 'LOAD_DOCTORS':
      return {
        ...state,
        doctors: action.payload,
        doctorsLoaded: true
      };

    case 'ADD_DEPARTMENT':
      return { ...state, departments: [...state.departments, action.payload] };
    case 'UPDATE_DEPARTMENT':
      return {
        ...state,
        departments: state.departments.map(department => department.id === action.payload.id ? action.payload : department)
      };
    case 'DELETE_DEPARTMENT':
      return {
        ...state,
        departments: state.departments.filter(department => department.id !== action.payload)
      };
    case 'LOAD_DEPARTMENTS':
      return {
        ...state,
        departments: action.payload,
        departmentsLoaded: true
      };
    case 'SET_INITIAL_LOAD_COMPLETE':
      return {
        ...state,
        initialLoadComplete: true
      };
    case 'ADD_BOARD_MEMBER':
      return { ...state, boardMembers: [...state.boardMembers, action.payload] };
    case 'UPDATE_BOARD_MEMBER':
      return {
        ...state,
        boardMembers: state.boardMembers.map(member => member.id === action.payload.id ? action.payload : member)
      };
    case 'DELETE_BOARD_MEMBER':
      return {
        ...state,
        boardMembers: state.boardMembers.filter(member => member.id !== action.payload)
      };
    case 'REORDER_BOARD_MEMBERS':
      return {
        ...state,
        boardMembers: action.payload
      };
    case 'LOAD_BOARD_MEMBERS':
      return {
        ...state,
        boardMembers: action.payload && action.payload.length > 0 ? action.payload : state.boardMembers
      };
    case 'ADD_TEAM_MEMBER':
      return { ...state, teamMembers: [...state.teamMembers, action.payload] };
    case 'UPDATE_TEAM_MEMBER':
      return {
        ...state,
        teamMembers: state.teamMembers.map(member => member.id === action.payload.id ? action.payload : member)
      };
    case 'DELETE_TEAM_MEMBER':
      return {
        ...state,
        teamMembers: state.teamMembers.filter(member => member.id !== action.payload)
      };
    case 'REORDER_TEAM_MEMBERS':
      return {
        ...state,
        teamMembers: action.payload
      };
    case 'LOAD_TEAM_MEMBERS':
      return {
        ...state,
        teamMembers: action.payload && action.payload.length > 0 ? action.payload : state.teamMembers
      };
    case 'LOAD_JOBS':
      return {
        ...state,
        jobs: action.payload
      };
    case 'LOAD_JOB_APPLICATIONS':
      return {
        ...state,
        jobApplications: action.payload
      };
    case 'LOAD_SERVICES': {
      if (!action.payload || action.payload.length === 0) {
        return state;
      }
      const merged = action.payload.map((apiService) => {
        const match = state.services.find(
          (d) =>
            d.id === apiService.id ||
            (d.name && apiService.name && d.name.toLowerCase() === apiService.name.toLowerCase())
        );
        return {
          ...match,
          ...apiService,
          name: apiService.name || match?.name || '',
          description: apiService.description || match?.description || '',
          icon: apiService.icon || match?.icon || 'medical_services',
          route:
            apiService.route && apiService.route !== '/services'
              ? apiService.route
              : match?.route || '/services',
          isActive: apiService.isActive !== false && apiService.is_active !== 0,
        };
      });
      return { ...state, services: merged };
    }
    case 'LOAD_HOSPITAL_INFO':
      return {
        ...state,
        hospitalInfo: action.payload
      };
    case 'LOAD_CEO':
      return {
        ...state,
        ceo: action.payload
      };
    default:
      return state;
  }
};

// API operations for doctors
const doctorApiOperations = {
  async addDoctor(doctorData, imageFile = null) {
    try {
      const result = await apiClient.addDoctor(doctorData, imageFile);
      return { success: true, doctorData: result.data };
    } catch (error) {
      console.error('Error adding doctor via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateDoctor(doctorData, imageFile = null) {
    try {
      let result;
      if (imageFile) {
        result = await apiClient.updateDoctorWithImage(doctorData, imageFile);
      } else {
        result = await apiClient.updateDoctor(doctorData);
      }
      return { success: true, doctorData: result.data };
    } catch (error) {
      console.error('Error updating doctor via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteDoctor(doctorId) {
    try {
      await apiClient.deleteDoctor(doctorId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting doctor via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadDoctors() {
    try {
      const result = await apiClient.getDoctors();
      return { success: true, doctors: result.data };
    } catch (error) {
      console.error('Error loading doctors via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Department API operations
const departmentApiOperations = {
  async addDepartment(departmentData, heroImageFile = null, iconFile = null) {
    try {
      let result;
      // If we have any file to upload, use the with-image endpoint
      if (heroImageFile || iconFile) {
        result = await apiClient.addDepartmentWithImage(departmentData, heroImageFile, iconFile);
      } else {
        // Otherwise use regular JSON endpoint
        result = await apiClient.addDepartment(departmentData);
      }
      // Normalize the response from camelCase to snake_case for consistency
      const normalizedData = this.normalizeDepartmentResponse(result.data);
      return { success: true, departmentData: normalizedData };
    } catch (error) {
      console.error('Error adding department via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateDepartment(departmentData, heroImageFile = null, iconFile = null) {
    try {
      let result;
      // If we have any file to upload, use the with-image endpoint
      if (heroImageFile || iconFile) {
        result = await apiClient.updateDepartmentWithImage(departmentData.id, departmentData, heroImageFile, iconFile);
      } else {
        // Otherwise use regular JSON endpoint
        result = await apiClient.updateDepartment(departmentData.id, departmentData);
      }
      // Normalize the response from camelCase to snake_case for consistency
      const normalizedData = this.normalizeDepartmentResponse(result.data);
      return { success: true, departmentData: normalizedData };
    } catch (error) {
      console.error('Error updating department via API:', error);
      return { success: false, error: error.message };
    }
  },

  normalizeDepartmentResponse(data) {
    return {
      ...data,
      is_active: ('isActive' in data) ? data.isActive : data.is_active,
      show_phone: ('showPhone' in data) ? data.showPhone : data.show_phone,
      show_head_of_department: ('showHeadOfDepartment' in data) ? data.showHeadOfDepartment : data.show_head_of_department,
      head_of_department_id: ('headOfDepartmentId' in data) ? data.headOfDepartmentId : data.head_of_department_id,
      head_of_department_name: ('headOfDepartmentName' in data) ? data.headOfDepartmentName : data.head_of_department_name,
      department_info: ('departmentInfo' in data) ? data.departmentInfo : data.department_info,
      service_category: ('serviceCategory' in data) ? data.serviceCategory : data.service_category,
      hero_image_id: ('heroImageId' in data) ? data.heroImageId : data.hero_image_id
    };
  },

  async deleteDepartment(departmentId) {
    try {
      await apiClient.deleteDepartment(departmentId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting department via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadDepartments() {
    try {
      const result = await apiClient.getDepartments();
      // Normalize each department from camelCase to snake_case for consistency
      const normalizedDepartments = Array.isArray(result.data) 
        ? result.data.map(dept => this.normalizeDepartmentResponse(dept))
        : result.data;
      return { success: true, departments: normalizedDepartments };
    } catch (error) {
      console.error('Error loading departments via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Board Members API operations
const boardMembersApiOperations = {
  async addBoardMember(boardMemberData, imageFile = null) {
    try {
      const result = await apiClient.addBoardMember(boardMemberData, imageFile);
      return { success: true, boardMemberData: result.data };
    } catch (error) {
      console.error('Error adding board member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateBoardMember(boardMemberData, imageFile = null) {
    try {
      const result = await apiClient.updateBoardMember(boardMemberData.id, boardMemberData, imageFile);
      return { success: true, boardMemberData: result.data };
    } catch (error) {
      console.error('Error updating board member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteBoardMember(boardMemberId) {
    try {
      await apiClient.deleteBoardMember(boardMemberId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting board member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadBoardMembers() {
    try {
      const result = await apiClient.getBoardMembers();
      return { success: true, boardMembers: result.data };
    } catch (error) {
      console.error('Error loading board members via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Team Members API operations
const teamMembersApiOperations = {
  async addTeamMember(teamMemberData, imageFile = null) {
    try {
      const result = await apiClient.addTeamMember(teamMemberData, imageFile);
      return { success: true, teamMemberData: result.data };
    } catch (error) {
      console.error('Error adding team member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateTeamMember(teamMemberData, imageFile = null) {
    try {
      const result = await apiClient.updateTeamMember(teamMemberData.id, teamMemberData, imageFile);
      return { success: true, teamMemberData: result.data };
    } catch (error) {
      console.error('Error updating team member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteTeamMember(teamMemberId) {
    try {
      await apiClient.deleteTeamMember(teamMemberId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting team member via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadTeamMembers() {
    try {
      const result = await apiClient.getTeamMembers();
      return { success: true, teamMembers: result.data };
    } catch (error) {
      console.error('Error loading team members via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Jobs API operations
const jobsApiOperations = {
  async addJob(jobData) {
    try {
      const result = await apiClient.addJob(jobData);
      return { success: true, jobData: result.data };
    } catch (error) {
      console.error('Error adding job via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateJob(jobData) {
    try {
      const result = await apiClient.updateJob(jobData.id, jobData);
      return { success: true, jobData: result.data };
    } catch (error) {
      console.error('Error updating job via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteJob(jobId) {
    try {
      await apiClient.deleteJob(jobId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting job via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadJobs() {
    try {
      const result = await apiClient.getJobs();
      return { success: true, jobs: result.data };
    } catch (error) {
      console.error('Error loading jobs via API:', error);
      return { success: false, error: error.message };
    }
  }
};

const normalizeJobApplication = (row) => {
  const resumeFilename = row.resume_filename ?? row.resumeFilename ?? '';
  const hasResume = Boolean(
    row.has_resume ?? row.hasResume ?? resumeFilename
  );

  return {
    id: row.id,
    jobId: row.job_id ?? row.jobId ?? null,
    department: row.department ?? row.job_department ?? row.jobDepartment ?? '',
    firstName: row.first_name ?? row.firstName ?? '',
    lastName: row.last_name ?? row.lastName ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    currentCompany: row.current_company ?? row.currentCompany ?? '',
    currentPosition: row.current_position ?? row.currentPosition ?? '',
    experience: row.experience ?? '',
    education: row.education ?? '',
    expectedSalary: row.expected_salary ?? row.expectedSalary ?? '',
    noticePeriod: row.notice_period ?? row.noticePeriod ?? '',
    source: row.source ?? '',
    coverLetter: row.cover_letter ?? row.coverLetter ?? '',
    resumeFilename,
    resumePath: row.resume_path ?? row.resumePath ?? '',
    hasResume,
    status: row.status ?? 'pending',
    createdAt: row.created_at ?? row.createdAt ?? null,
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    jobTitle: row.job_title ?? row.jobTitle ?? '',
    jobDepartment: row.job_department ?? row.jobDepartment ?? '',
  };
};

const dedupeJobApplications = (applications) => {
  const skipIds = new Set();

  for (let i = 0; i < applications.length; i++) {
    if (skipIds.has(applications[i].id)) continue;

    for (let j = i + 1; j < applications.length; j++) {
      const a = applications[i];
      const b = applications[j];
      if (skipIds.has(b.id)) continue;
      if (a.email !== b.email) continue;
      if (String(a.jobId || '') !== String(b.jobId || '')) continue;
      if ((a.department || '') !== (b.department || '')) continue;

      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      if (!Number.isFinite(timeA) || !Number.isFinite(timeB) || Math.abs(timeA - timeB) > 30000) {
        continue;
      }

      if (a.hasResume && !b.hasResume) {
        skipIds.add(b.id);
      } else if (!a.hasResume && b.hasResume) {
        skipIds.add(a.id);
        break;
      } else {
        skipIds.add(b.id);
      }
    }
  }

  return applications
    .filter((app) => !skipIds.has(app.id))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// Job Applications API operations
const jobApplicationsApiOperations = {
  async addJobApplication(applicationData) {
    try {
      const result = await apiClient.addJobApplication(applicationData);
      return { success: true, applicationData: result.data };
    } catch (error) {
      console.error('Error adding job application via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateJobApplication(applicationData) {
    try {
      const result = await apiClient.updateJobApplication(applicationData.id, applicationData);
      return { success: true, applicationData: result.data };
    } catch (error) {
      console.error('Error updating job application via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteJobApplication(applicationId) {
    try {
      await apiClient.deleteJobApplication(applicationId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting job application via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadJobApplications() {
    try {
      const result = await apiClient.getJobApplications();
      const applications = Array.isArray(result.data)
        ? dedupeJobApplications(result.data.map(normalizeJobApplication))
        : [];
      return { success: true, applications };
    } catch (error) {
      console.error('Error loading job applications via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateJobApplicationStatus(applicationId, status) {
    try {
      const result = await apiClient.updateJobApplicationStatus(applicationId, status);
      return { success: true, status: result.data?.status || status };
    } catch (error) {
      console.error('Error updating job application status via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Services API operations
const servicesApiOperations = {
  async addService(serviceData, imageFile = null) {
    try {
      const result = await apiClient.addService(serviceData, imageFile);
      return { success: true, serviceData: result.data };
    } catch (error) {
      console.error('Error adding service via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateService(serviceData, imageFile = null) {
    try {
      const result = await apiClient.updateService(serviceData.id, serviceData, imageFile);
      return { success: true, serviceData: result.data };
    } catch (error) {
      console.error('Error updating service via API:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteService(serviceId) {
    try {
      await apiClient.deleteService(serviceId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting service via API:', error);
      return { success: false, error: error.message };
    }
  },

  async loadServices() {
    try {
      const result = await apiClient.getServices();
      return { success: true, services: result.data };
    } catch (error) {
      console.error('Error loading services via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// Hospital Info API operations
const hospitalInfoApiOperations = {
  async loadHospitalInfo() {
    try {
      const result = await apiClient.getHospitalInfo();
      return { success: true, hospitalInfo: result.data };
    } catch (error) {
      console.error('Error loading hospital info via API:', error);
      return { success: false, error: error.message };
    }
  },

  async updateHospitalInfo(hospitalInfoData) {
    try {
      const result = await apiClient.updateHospitalInfo(hospitalInfoData);
      return { success: true, hospitalInfo: result.data };
    } catch (error) {
      console.error('Error updating hospital info via API:', error);
      return { success: false, error: error.message };
    }
  }
};

// CEO API operations
const ceoApiOperations = {
  async loadCEO() {
    try {
      const result = await apiClient.getCEO();
      return { success: true, ceo: result.data };
    } catch (error) {
      console.error('Error loading CEO via API:', error);
      return { success: false, error: error.message };
    }
  }
};

export const DataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // Load all critical data in parallel and only hide loader when all are complete
  useEffect(() => {
    // Load all critical data in parallel
    const loadAllCriticalData = async () => {
      const promises = [];

      // Load doctors
      promises.push(
        doctorApiOperations.loadDoctors().then(result => {
          if (result.success) {
            dispatch({ type: 'LOAD_DOCTORS', payload: result.doctors });
          } else {
            dispatch({ type: 'LOAD_DOCTORS', payload: [] });
          }
        }).catch(error => {
          console.error('Error loading doctors:', error);
          dispatch({ type: 'LOAD_DOCTORS', payload: [] });
        })
      );

      // Load posts
      promises.push(
        apiClient.getPosts().then(result => {
          if (result && result.success) {
            dispatch({ type: 'LOAD_POSTS', payload: result.data });
          } else {
            dispatch({ type: 'LOAD_POSTS', payload: [] });
          }
        }).catch(error => {
          console.error('Error loading posts:', error);
          dispatch({ type: 'LOAD_POSTS', payload: [] });
        })
      );

      // Load departments
      promises.push(
        departmentApiOperations.loadDepartments().then(result => {
          if (result.success) {
            dispatch({ type: 'LOAD_DEPARTMENTS', payload: result.departments });
          } else {
            console.error('Failed to load departments from API:', result.error);
            dispatch({ type: 'LOAD_DEPARTMENTS', payload: [] });
          }
        }).catch(error => {
          console.error('Error loading departments:', error);
          dispatch({ type: 'LOAD_DEPARTMENTS', payload: [] });
        })
      );

      // Wait for all critical data to load
      await Promise.all(promises);
      
      dispatch({ type: 'SET_INITIAL_LOAD_COMPLETE' });
    };

    loadAllCriticalData();
  }, []);

  // Load board members from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadBoardMembersFromApi = async () => {
      try {
        const result = await boardMembersApiOperations.loadBoardMembers();
        if (result.success) {
          dispatch({ type: 'LOAD_BOARD_MEMBERS', payload: result.boardMembers });
        } else {
          console.error('Failed to load board members from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading board members from API:', error);
      }
    };
    
    loadBoardMembersFromApi();
  }, []);

  // Load team members from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadTeamMembersFromApi = async () => {
      try {
        const result = await teamMembersApiOperations.loadTeamMembers();
        if (result.success) {
          dispatch({ type: 'LOAD_TEAM_MEMBERS', payload: result.teamMembers });
        } else {
          console.error('Failed to load team members from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading team members from API:', error);
      }
    };
    
    loadTeamMembersFromApi();
  }, []);

  // Load jobs from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadJobsFromApi = async () => {
      try {
        const result = await jobsApiOperations.loadJobs();
        if (result.success) {
          dispatch({ type: 'LOAD_JOBS', payload: result.jobs });
        } else {
          console.error('Failed to load jobs from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading jobs from API:', error);
      }
    };
    
    loadJobsFromApi();
  }, []);

  // Load job applications from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadJobApplicationsFromApi = async () => {
      try {
        const result = await jobApplicationsApiOperations.loadJobApplications();
        if (result.success) {
          dispatch({ type: 'LOAD_JOB_APPLICATIONS', payload: result.applications });
        } else {
          console.error('Failed to load job applications from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading job applications from API:', error);
      }
    };
    
    loadJobApplicationsFromApi();
  }, []);

  // Load services from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadServicesFromApi = async () => {
      try {
        const result = await servicesApiOperations.loadServices();
        if (result.success) {
          dispatch({ type: 'LOAD_SERVICES', payload: result.services });
        } else {
          console.error('Failed to load services from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading services from API:', error);
      }
    };
    
    loadServicesFromApi();
  }, []);

  // Load hospital info from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadHospitalInfoFromApi = async () => {
      try {
        const result = await hospitalInfoApiOperations.loadHospitalInfo();
        if (result.success) {
          dispatch({ type: 'LOAD_HOSPITAL_INFO', payload: result.hospitalInfo });
        } else {
          console.error('Failed to load hospital info from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading hospital info from API:', error);
      }
    };
    
    loadHospitalInfoFromApi();
  }, []);

  // Load CEO from API on mount (non-critical, loads in background)
  useEffect(() => {
    const loadCEOFromApi = async () => {
      try {
        const result = await ceoApiOperations.loadCEO();
        if (result.success) {
          dispatch({ type: 'LOAD_CEO', payload: result.ceo });
        } else {
          console.error('Failed to load CEO from API:', result.error);
        }
      } catch (error) {
        console.error('Error loading CEO from API:', error);
      }
    };
    
    loadCEOFromApi();
  }, []);

  // Enhanced dispatch with API operations
  const enhancedDispatch = async (action) => {
    switch (action.type) {
      case 'ADD_POST': {
        try {
          const resp = await apiClient.addPost(action.payload);
          if (resp && resp.success) {
            dispatch({ type: 'ADD_POST', payload: resp.data });
            return { success: true };
          }
          return { success: false, error: 'Failed to add post' };
        } catch (e) {
          return { success: false, error: e.message };
        }
      }

      case 'UPDATE_POST': {
        try {
          const resp = await apiClient.updatePost(action.payload);
          if (resp && resp.success) {
            dispatch({ type: 'UPDATE_POST', payload: resp.data });
            return { success: true };
          }
          return { success: false, error: 'Failed to update post' };
        } catch (e) {
          return { success: false, error: e.message };
        }
      }

      case 'DELETE_POST': {
        try {
          const resp = await apiClient.deletePost(action.payload);
          if (resp && resp.success) {
            dispatch({ type: 'DELETE_POST', payload: action.payload });
            return { success: true };
          }
          return { success: false, error: 'Failed to delete post' };
        } catch (e) {
          return { success: false, error: e.message };
        }
      }
      case 'ADD_DOCTOR':
        try {
          const result = await doctorApiOperations.addDoctor(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'ADD_DOCTOR', payload: result.doctorData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_DOCTOR':
        try {
          const result = await doctorApiOperations.updateDoctor(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'UPDATE_DOCTOR', payload: result.doctorData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_DOCTOR':
        try {
          const result = await doctorApiOperations.deleteDoctor(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_DOCTOR', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_DEPARTMENT':
        try {
          const result = await departmentApiOperations.addDepartment(action.payload, action.heroImageFile, action.iconFile);
          if (result.success) {
            dispatch({ type: 'ADD_DEPARTMENT', payload: result.departmentData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_DEPARTMENT':
        try {
          const result = await departmentApiOperations.updateDepartment(action.payload, action.heroImageFile, action.iconFile);
          if (result.success) {
            dispatch({ type: 'UPDATE_DEPARTMENT', payload: result.departmentData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_DEPARTMENT':
        try {
          const result = await departmentApiOperations.deleteDepartment(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_DEPARTMENT', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_BOARD_MEMBER':
        try {
          const result = await boardMembersApiOperations.addBoardMember(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'ADD_BOARD_MEMBER', payload: result.boardMemberData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_BOARD_MEMBER':
        try {
          const result = await boardMembersApiOperations.updateBoardMember(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'UPDATE_BOARD_MEMBER', payload: result.boardMemberData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_BOARD_MEMBER':
        try {
          const result = await boardMembersApiOperations.deleteBoardMember(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_BOARD_MEMBER', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_TEAM_MEMBER':
        try {
          const result = await teamMembersApiOperations.addTeamMember(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'ADD_TEAM_MEMBER', payload: result.teamMemberData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_TEAM_MEMBER':
        try {
          const result = await teamMembersApiOperations.updateTeamMember(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'UPDATE_TEAM_MEMBER', payload: result.teamMemberData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_TEAM_MEMBER':
        try {
          const result = await teamMembersApiOperations.deleteTeamMember(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_TEAM_MEMBER', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_JOB':
        try {
          const result = await jobsApiOperations.addJob(action.payload);
          if (result.success) {
            dispatch({ type: 'ADD_JOB', payload: result.jobData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_JOB':
        try {
          const result = await jobsApiOperations.updateJob(action.payload);
          if (result.success) {
            dispatch({ type: 'UPDATE_JOB', payload: result.jobData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_JOB':
        try {
          const result = await jobsApiOperations.deleteJob(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_JOB', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_JOB_APPLICATION':
        try {
          const result = await jobApplicationsApiOperations.addJobApplication(action.payload);
          if (result.success) {
            dispatch({
              type: 'ADD_JOB_APPLICATION',
              payload: normalizeJobApplication(result.applicationData),
            });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_JOB_APPLICATION_LOCAL':
        dispatch({
          type: 'ADD_JOB_APPLICATION_LOCAL',
          payload: normalizeJobApplication(action.payload),
        });
        return { success: true };

      case 'UPDATE_JOB_APPLICATION_STATUS':
        try {
          const result = await jobApplicationsApiOperations.updateJobApplicationStatus(
            action.payload.id,
            action.payload.status
          );
          if (result.success) {
            const application = state.jobApplications.find((app) => app.id === action.payload.id);
            if (application) {
              dispatch({
                type: 'UPDATE_JOB_APPLICATION',
                payload: {
                  ...application,
                  status: action.payload.status,
                  updatedAt: new Date().toISOString(),
                },
              });
            }
            return { success: true };
          }
          return { success: false, error: result.error };
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_JOB_APPLICATION':
        try {
          const result = await jobApplicationsApiOperations.updateJobApplication(action.payload);
          if (result.success) {
            dispatch({ type: 'UPDATE_JOB_APPLICATION', payload: result.applicationData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_JOB_APPLICATION':
        try {
          const result = await jobApplicationsApiOperations.deleteJobApplication(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_JOB_APPLICATION', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'ADD_SERVICE':
        try {
          const result = await servicesApiOperations.addService(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'ADD_SERVICE', payload: result.serviceData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_SERVICE':
        try {
          const result = await servicesApiOperations.updateService(action.payload, action.imageFile);
          if (result.success) {
            dispatch({ type: 'UPDATE_SERVICE', payload: result.serviceData });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'DELETE_SERVICE':
        try {
          const result = await servicesApiOperations.deleteService(action.payload);
          if (result.success) {
            dispatch({ type: 'DELETE_SERVICE', payload: action.payload });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      case 'UPDATE_HOSPITAL_INFO':
        try {
          const result = await hospitalInfoApiOperations.updateHospitalInfo(action.payload);
          if (result.success) {
            dispatch({ type: 'UPDATE_HOSPITAL_INFO', payload: result.hospitalInfo });
            return { success: true };
          } else {
            return { success: false, error: result.error };
          }
        } catch (error) {
          return { success: false, error: error.message };
        }

      default:
        dispatch(action);
        return { success: true };
    }
  };

  // Load data from localStorage on mount (for non-database data)
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('hospitalData');
      if (!savedData) return;

      const parsedData = JSON.parse(savedData);
      Object.keys(parsedData).forEach(key => {
        if (key !== 'doctors' && key !== 'departments' && key !== 'services' && key !== 'jobApplications' && parsedData[key] !== state[key]) {
          dispatch({ type: `LOAD_${key.toUpperCase()}`, payload: parsedData[key] });
        }
      });
    } catch (error) {
      console.warn('Failed to restore hospitalData cache, clearing invalid cache:', error);
      localStorage.removeItem('hospitalData');
    }
  }, []);

  // Load board members from database on mount
  useEffect(() => {
    const loadBoardMembers = async () => {
      try {
        const result = await boardMembersApiOperations.loadBoardMembers();
        if (result.success) {
          dispatch({ type: 'LOAD_BOARD_MEMBERS', payload: result.boardMembers });
        }
      } catch (error) {
        console.error('Error loading board members:', error);
      }
    };
    loadBoardMembers();
  }, []);

  // Load team members from database on mount
  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        const result = await teamMembersApiOperations.loadTeamMembers();
        if (result.success) {
          dispatch({ type: 'LOAD_TEAM_MEMBERS', payload: result.teamMembers });
        }
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };
    loadTeamMembers();
  }, []);

  // Save non-database data to localStorage whenever state changes
  useEffect(() => {
    const dataToSave = { ...state };
    delete dataToSave.doctors; // Don't save doctors to localStorage as they're in database
    delete dataToSave.departments; // Don't save departments to localStorage as they're in database
    delete dataToSave.services; // Don't save services to localStorage as they're static
    delete dataToSave.jobApplications; // Can contain large resume data payloads

    try {
      localStorage.setItem('hospitalData', JSON.stringify(dataToSave));
    } catch (error) {
      // Avoid crashing the app if browser storage quota is exceeded.
      if (error && (error.name === 'QuotaExceededError' || error.code === 22)) {
        console.warn('hospitalData cache skipped: localStorage quota exceeded');
      } else {
        console.warn('Failed to save hospitalData cache:', error);
      }
    }
  }, [state]);

  return (
    <DataContext.Provider value={{ state, dispatch: enhancedDispatch }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};